import { useState } from "react";
import { Button, CircularProgress, Stack, TextField } from "@mui/material";
import { ResponsiveDialog } from "Components/Shared/Dialogs/ResponsiveDialog";
import { toast } from "sonner";
import { useRenameChatChannelMutation } from "Api/Mutations/Chat/useRenameChatChannelMutation";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Chat.Rename;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channelId: string;
  currentName: string;
};

export const RenameChannelDialog = ({
  isOpen,
  onClose,
  channelId,
  currentName,
}: Props) => {
  const [name, setName] = useState(currentName);
  const { t } = useResource();

  const { mutate: renameChannel, isPending } = useRenameChatChannelMutation({
    onSuccess: onClose,
    onError: () => toast.error(t(RESOURCES.Error)),
  });

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }

    renameChannel({ channelId, name: trimmed });
  };

  const handleClose = () => {
    setName(currentName);
    onClose();
  };

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t(RESOURCES.Title)}
      footer={
        <Stack direction="row" justifyContent="flex-end" gap={2}>
          <Button onClick={handleClose}>{t(RESOURCES.Cancel)}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!name.trim() || isPending}
            startIcon={isPending ? <CircularProgress size={20} /> : undefined}
          >
            {t(RESOURCES.Save)}
          </Button>
        </Stack>
      }
    >
      <TextField
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        autoFocus
        slotProps={{ htmlInput: { maxLength: 100 } }}
      />
    </ResponsiveDialog>
  );
};
